import type { ReactNode } from 'react';
import { createRoot } from 'react-dom/client';

// Longest the static snapshot stays up if the live page never settles.
const MAX_WAIT_MS = 4000;
// Longest the swap waits on above-the-fold images once nothing is loading any more.
const IMAGE_WAIT_MS = 1000;

type SettleState = 'loading' | 'images' | 'settled';

function settleState(container: HTMLElement, snapshot: HTMLElement): SettleState {
  if (!container.firstElementChild) return 'loading';

  // Suspense fallbacks and the loading header use LoadingIndicator (role="status");
  // page skeletons use animate-pulse.
  if (container.querySelector('[role="status"], .animate-pulse')) return 'loading';

  // Don't reveal while an above-the-fold image would still pop in.
  const pending = Array.from(container.querySelectorAll('img')).filter(img => {
    if (img.complete) return false;
    const rect = img.getBoundingClientRect();
    return rect.width > 0 && rect.top < window.innerHeight && rect.bottom > 0;
  });
  if (pending.length === 0) return 'settled';

  // An image the snapshot also has (loaded or still loading) shares its download, so
  // swapping can't make it appear any later than it would in the snapshot.
  const inSnapshot = new Set(Array.from(snapshot.querySelectorAll('img')).map(img => img.src));
  return pending.every(img => inSnapshot.has(img.src)) ? 'settled' : 'images';
}

/**
 * The prerendered HTML already shows the finished page. createRoot().render() on
 * that container would wipe it and flash the Suspense spinner and skeletons before
 * the page comes back. Instead, render the live app off-screen next to the snapshot
 * and swap it in once it has no loading states left.
 *
 * The swap replaces the hero H1, so it can count as the page's Largest Contentful Paint
 * if it lands late. That's why slow images only hold it back for IMAGE_WAIT_MS.
 */
export function renderBehindSnapshot(snapshot: HTMLElement, app: ReactNode) {
  const live = document.createElement('div');
  live.style.cssText = 'position:absolute;top:0;left:0;width:100%;visibility:hidden;pointer-events:none';
  live.setAttribute('aria-hidden', 'true');
  snapshot.after(live);

  let revealed = false;
  const reveal = () => {
    if (revealed) return;
    revealed = true;
    observer.disconnect();
    clearTimeout(timeout);
    clearTimeout(imageTimeout);
    live.removeEventListener('load', check, true);
    live.removeEventListener('error', check, true);

    snapshot.remove();
    live.removeAttribute('style');
    live.removeAttribute('aria-hidden');
    live.id = 'root';
  };

  let imageTimeout: ReturnType<typeof setTimeout> | undefined;
  function check() {
    const state = settleState(live, snapshot);
    if (state === 'settled') reveal();
    else if (state === 'images' && imageTimeout === undefined) {
      imageTimeout = setTimeout(() => {
        if (settleState(live, snapshot) === 'loading') imageTimeout = undefined;
        else reveal();
      }, IMAGE_WAIT_MS);
    }
  }

  const observer = new MutationObserver(check);
  observer.observe(live, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'role'] });
  live.addEventListener('load', check, true);
  live.addEventListener('error', check, true);
  const timeout = setTimeout(reveal, MAX_WAIT_MS);

  createRoot(live).render(app);
}
